import React, { useMemo } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../../constants";

function ExcelImage({ image, colWidths, rowHeights, zoomScale, hiddenCols, hiddenRows }) {
  // Compute position and size synchronously to avoid a flash at (0,0)
  const { position, imageSize } = useMemo(() => {
    if (!image.range) return { position: { top: 0, left: 0 }, imageSize: { width: image.width, height: image.height } };

    const range = image.range;
    const headerRowHeight = HEADER_ROW_HEIGHT;
    const headerColWidth = HEADER_COL_WIDTH; // 44px - matches CSS

    // Hidden rows/cols are not rendered by the grid — skip them here too.
    const isColHidden = (i) => !!(hiddenCols && hiddenCols.has && hiddenCols.has(i));
    const isRowHidden = (i) => !!(hiddenRows && hiddenRows.has && hiddenRows.has(i));

    // Calculate top-left position
    let tlLeft = 0, tlTop = 0;
    for (let i = 0; i < (range.tl?.col || 0); i++) {
      if (!isColHidden(i)) tlLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    for (let i = 0; i < (range.tl?.row || 0); i++) {
      if (!isRowHidden(i)) tlTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }

    // Add header offsets
    tlLeft += headerColWidth;
    tlTop += headerRowHeight;

    // Add EMU sub-cell offsets
    if (range.tl?.colOff) tlLeft += range.tl.colOff / 914400 * 96;
    if (range.tl?.rowOff) tlTop += range.tl.rowOff / 914400 * 96;

    let width = image.width;
    let height = image.height;

    // Calculate dimensions from bottom-right anchor
    if (range.br) {
      let brLeft = 0, brTop = 0;
      for (let i = 0; i < (range.br.col || 0); i++) {
        if (!isColHidden(i)) brLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
      }
      for (let i = 0; i < (range.br.row || 0); i++) {
        if (!isRowHidden(i)) brTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      }
      brLeft += headerColWidth;
      brTop += headerRowHeight;

      if (range.br.colOff) brLeft += range.br.colOff / 914400 * 96;
      if (range.br.rowOff) brTop += range.br.rowOff / 914400 * 96;

      width = Math.max(20, brLeft - tlLeft);
      height = Math.max(20, brTop - tlTop);
    }

    return { position: { top: tlTop, left: tlLeft }, imageSize: { width, height } };
  }, [image, colWidths, rowHeights, hiddenCols, hiddenRows]);

  // Scale both position AND size by zoomScale so overlay aligns with the zoomed table
  const scaledTop = position.top * zoomScale;
  const scaledLeft = position.left * zoomScale;
  const scaledWidth = imageSize.width * zoomScale;
  const scaledHeight = imageSize.height * zoomScale;

  return (
    <div
      className="excel-image-overlay"
      style={{
        position: 'absolute',
        top: scaledTop,
        left: scaledLeft,
        width: scaledWidth,
        height: scaledHeight,
        pointerEvents: 'auto',
        zIndex: 10,
      }}
    >
      <img
        src={image.dataUrl}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: 'none',
          maxHeight: 'none',
        }}
        draggable={false}
      />
    </div>
  );
}

// PERF: memo'd — image objects and size arrays are stable per sheet.
export default React.memo(ExcelImage);
